import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../util';
import Load from '~/components/Load';

const buttonVariants = cva(
    'inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all focus:outline-none focus:ring-2 focus:ring-secondary disabled:pointer-events-none disabled:opacity-50 hover:scale-105 hover:shadow-lg',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground hover:bg-accent',
                confirm:
                    'bg-confirm text-confirm-foreground hover:bg-confirm/90',
                destructive: 'bg-destructive text-destructive-foreground ',
                outline:
                    'border border-input bg-background hover:bg-accent hover:text-primary-foreground',
                secondary:
                    'bg-secondary text-secondary-foreground hover:text-primary-foreground',
                ghost: 'hover:bg-accent hover:text-primary-foreground focus:ring-primary',
                link: 'text-primary underline-offset-4 hover:underline',
            },
            size: {
                default: 'h-10 px-4 py-2',
                sm: 'clamp-[rounded-sm-md-clamp] clamp-[h-8-9-clamp] clamp-[px-2.5-3-clamp] clamp-[text-xs-sm-clamp]',
                lg: 'h-11 rounded-md px-8',
                icon: 'h-10 w-10',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

export interface ButtonProps
    extends
        React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            loading = false,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : 'button';
        if (loading) return <Load />;
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        );
    },
);
Button.displayName = 'Button';

export { Button };
